import * as THREE from 'three';
import type { MapDefinition, Point, Waypoint } from '../../../shared/game/world-types.js';
import { levelHeight } from '../../../shared/game/caves.js';
import { groundDecal, groundUi } from './ground-decal.js';
import { withinReach } from './terrain-decals.js';

/** Painted dashes along the route, in metres: length, gap, half-width, and how fast they drift toward the destination (m/s). */
const DASH = .24, GAP = .2, HALF = .04, DRIFT = .35;
/** The dashes fade in over this distance from the hero's feet and fade toward the destination, down to `END_ALPHA`. */
const FEET = .45, START_ALPHA = .85, END_ALPHA = .45;
/** Queued legs and their stops are fainter: they wait behind the order under way. */
const QUEUED_ALPHA = .38;
/** The destination: a ring round a dot, which settles in from wider over `SETTLE` seconds when the order changes. */
const RING = .36, RING_WIDTH = .055, DOT = .065, SETTLE = .35, QUEUED_RING = .24;
/** The notch outside a formation slot's ring that points the way the hero will face there: from the ring out to its tip, and its half-width. */
const NOTCH_BASE = .07, NOTCH_TIP = .3, NOTCH_HALF = .11;
/** How far above the ground the marks sit, as the other ground decals do. */
const LIFT = .075;
/** How far a dash's far end may rise or fall from its near end (a cave mouth's rock wall must not stand a dash up on end). Rings and notches use `withinReach` of their centre. */
const DASH_STEP = .25;
/** Dashes where a sneaking hero would step into a foe's sight. */
const EXPOSED = new THREE.Color(0xe3a35c);
/** Vertices the mesh starts with; it grows by half again when a route needs more. */
const FIRST_CAPACITY = 2048;

/**
 * One hero's orders painted on the ground: a row of short dashes along the route under way that drift toward its destination, a ring
 * with a dot at the destination that settles in when the order is given, and the hero's queued moves as fainter legs ending in
 * smaller rings. A sneaking hero's dashes turn ochre where it would step into a foe's sight (`exposed`). It is one mesh in the hero's colour, rebuilt each frame from the route, so it
 * follows the terrain without a separate conforming pass. Each mark lies on the level its point is on (`Waypoint.level`, carried along the
 * route from the hero's own), so a click inside a cave is drawn on the cave's floor, not on the rock over it. Presentation only: it draws
 * what the snapshot says.
 *
 * The mesh has its position and four-component colour buffers from the start, so the shader warming compiles is the one drawing uses
 * (vertex alpha changes the program), and a frame's marks are written straight into them: nothing is allocated while drawing, and only
 * the written part is uploaded.
 */
export class OrderMarks {
  readonly mesh: THREE.Mesh;
  private positions = new Float32Array(FIRST_CAPACITY * 3);
  private colors = new Float32Array(FIRST_CAPACITY * 4);
  private count = 0;
  private lengths: number[] = [];
  private map: MapDefinition | null = null;
  /** The level the mark being written lies on, and the level each point of the polyline being dashed is reached on. */
  private level = 0;
  private levels: number[] = [];
  private destination: Point | null = null;
  private settledAt = -Infinity;
  /** Where `locate` last found a point, and the segment it stopped in. */
  private at = { x: 0, z: 0 };
  private segment = 1;
  /** A queued leg's end, reused as the one-point tail of its polyline. */
  private leg: Waypoint[] = [{ x: 0, z: 0 }];
  /** The hero's colour, written into each vertex so single dashes can take the exposed colour instead. */
  private tint: THREE.Color;
  private exposed?: (point: Point) => boolean;
  private shade: THREE.Color;

  constructor(color: number) {
    this.tint = new THREE.Color(color); this.shade = this.tint;
    const material = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, toneMapped: false });
    const geometry = new THREE.BufferGeometry();
    this.attach(geometry);
    this.mesh = groundUi(groundDecal(new THREE.Mesh(geometry, material)));
    this.mesh.frustumCulled = false; this.mesh.visible = false;
    geometry.setDrawRange(0, 0);
  }

  /**
   * Paints the route from `start` through `route`, then the queued moves after it; `exposed` marks ground a sneaking hero would be seen on,
   * and `facing` notches the destination ring the way the hero will face there (a formation's slot).
   */
  draw(map: MapDefinition, start: Point, route: readonly Waypoint[], queued: readonly Waypoint[], seconds = performance.now() / 1000, exposed?: (point: Point) => boolean, facing?: number, level = 0) {
    this.count = 0; this.map = map; this.exposed = exposed;
    const end = route.at(-1) ?? null;
    if (end && (!this.destination || Math.hypot(end.x - this.destination.x, end.z - this.destination.z) > .05)) this.settledAt = seconds;
    this.destination = end;
    if (end) {
      level = this.dashes(start, route, seconds, true, START_ALPHA, END_ALPHA, level);
      this.level = level;
      const settle = Math.min(1, (seconds - this.settledAt) / SETTLE), grow = 1 + (1 - settle) ** 2 * .7;
      this.ring(end, RING * grow, RING_WIDTH, Math.min(1, .35 + settle));
      this.ring(end, DOT, DOT, 1);
      if (facing !== undefined) this.notch(end, facing, RING * grow, Math.min(1, .35 + settle));
    }
    let from = end ?? start;
    for (const stop of queued) {
      this.leg[0] = stop;
      level = this.dashes(from, this.leg, seconds, false, QUEUED_ALPHA, QUEUED_ALPHA, level);
      this.level = level;
      this.ring(stop, QUEUED_RING, RING_WIDTH * .8, QUEUED_ALPHA);
      from = stop;
    }
    this.shade = this.tint;
    this.flush();
  }
  /** A formation slot the player is still turning: its ring, dot and facing notch, with no route to it. */
  drawSlot(map: MapDefinition, slot: Point, facing: number, level = 0) {
    this.count = 0; this.map = map; this.exposed = undefined; this.destination = null; this.level = level;
    this.ring(slot, RING, RING_WIDTH, .8); this.ring(slot, DOT, DOT, 1); this.notch(slot, facing, RING, .8);
    this.flush();
  }
  hide() { this.mesh.visible = false; this.destination = null; }
  dispose() { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); }

  /** Gives `geometry` the mark buffers at the current capacity. */
  private attach(geometry: THREE.BufferGeometry) {
    geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 4).setUsage(THREE.DynamicDrawUsage));
  }
  /** Room for `vertices` more, growing the buffers (and keeping what is written) when the route needs it. */
  private reserve(vertices: number) {
    const need = this.count + vertices, capacity = this.positions.length / 3;
    if (need <= capacity) return;
    const grown = Math.max(need, Math.ceil(capacity * 1.5));
    const positions = new Float32Array(grown * 3), colors = new Float32Array(grown * 4);
    positions.set(this.positions.subarray(0, this.count * 3)); colors.set(this.colors.subarray(0, this.count * 4));
    this.positions = positions; this.colors = colors;
    // Disposing first frees the buffers of the attributes being replaced.
    this.mesh.geometry.dispose(); this.attach(this.mesh.geometry);
  }
  private vertex(x: number, y: number, z: number, alpha: number) {
    const i = this.count++, p = i * 3, c = i * 4;
    this.positions[p] = x; this.positions[p + 1] = y; this.positions[p + 2] = z;
    this.colors[c] = this.shade.r; this.colors[c + 1] = this.shade.g; this.colors[c + 2] = this.shade.b; this.colors[c + 3] = alpha;
  }
  private ground0(x: number, z: number) { return levelHeight(this.map!, x, z, this.level); }
  /** The ground under a point of a mark centred at `centre` (a ground height), held near it so a rock wall beside it cannot bend the mark. */
  private height(x: number, z: number, centre: number) { return withinReach(this.ground0(x, z), centre) + LIFT; }

  /**
   * Dashes along a polyline of `head` then `tail`, which starts on `level` and changes level at the waypoints that name one; `fadeIn`
   * scales the alpha by distance from its start, which runs from `first` to `last` along it. Returns the level it ends on.
   */
  private dashes(head: Point, tail: readonly Waypoint[], seconds: number, fadeIn: boolean, first: number, last: number, level: number) {
    const lengths = this.lengths, levels = this.levels, points = tail.length + 1;
    lengths.length = points; lengths[0] = 0; levels.length = points; levels[0] = level;
    for (let i = 1; i < points; i++) levels[i] = tail[i - 1].level ?? levels[i - 1];
    for (let i = 1; i < points; i++) {
      const a = i === 1 ? head : tail[i - 2], b = tail[i - 1];
      lengths[i] = lengths[i - 1] + Math.hypot(b.x - a.x, b.z - a.z);
    }
    const total = lengths[points - 1], end = levels[points - 1];
    if (total < 1e-3) return end;
    this.segment = 1;
    const period = DASH + GAP, drift = (seconds * DRIFT) % period;
    for (let s = drift - period; s < total; s += period) {
      const from = Math.max(0, s), to = Math.min(total, s + DASH);
      if (to - from < .02) continue;
      const middle = (from + to) / 2, along = first + (last - first) * middle / total, alpha = fadeIn ? Math.min(Math.min(1, middle / FEET) * START_ALPHA, along) : Math.min(1, along);
      if (alpha <= .01) continue;
      this.locate(head, tail, from); const ax = this.at.x, az = this.at.z;
      this.level = levels[this.segment - 1];
      this.locate(head, tail, to);
      this.shade = this.exposed?.({ x: (ax + this.at.x) / 2, z: (az + this.at.z) / 2 }) ? EXPOSED : this.tint;
      this.strip(ax, az, this.at.x, this.at.z, alpha);
      this.shade = this.tint;
    }
    return end;
  }
  /** Finds the point `distance` along the polyline into `at`; `segment` carries over between nearby asks. */
  private locate(head: Point, tail: readonly Point[], distance: number) {
    const lengths = this.lengths, points = lengths.length;
    while (this.segment < points - 1 && lengths[this.segment] < distance) this.segment++;
    while (this.segment > 1 && lengths[this.segment - 1] > distance) this.segment--;
    const i = this.segment, a = i === 1 ? head : tail[i - 2], b = tail[i - 1], span = lengths[i] - lengths[i - 1], t = span > 0 ? (distance - lengths[i - 1]) / span : 0;
    this.at.x = a.x + (b.x - a.x) * t; this.at.z = a.z + (b.z - a.z) * t;
  }
  /** One dash: a flat quad between two ground points. */
  private strip(ax: number, az: number, bx: number, bz: number, alpha: number) {
    this.reserve(6);
    const length = Math.hypot(bx - ax, bz - az) || 1, nx = -(bz - az) / length * HALF, nz = (bx - ax) / length * HALF;
    const base = this.ground0(ax, az), ya = base + LIFT, yb = withinReach(this.ground0(bx, bz), base, DASH_STEP) + LIFT;
    // Corners 0–3 run a+n, a-n, b-n, b+n; the quad is the triangles 0 1 2 and 0 2 3.
    this.vertex(ax + nx, ya, az + nz, alpha); this.vertex(ax - nx, ya, az - nz, alpha); this.vertex(bx - nx, yb, bz - nz, alpha);
    this.vertex(ax + nx, ya, az + nz, alpha); this.vertex(bx - nx, yb, bz - nz, alpha); this.vertex(bx + nx, yb, bz + nz, alpha);
  }
  /** A ring (or, when `width` reaches the centre, a disc) lying on the ground. */
  private ring(centre: Point, radius: number, width: number, alpha: number) {
    const steps = radius > .15 ? 40 : 16, inner = Math.max(0, radius - width), centreY = this.ground0(centre.x, centre.z);
    this.reserve(steps * 6);
    for (let i = 0; i < steps; i++) {
      const a0 = i / steps * Math.PI * 2, a1 = (i + 1) / steps * Math.PI * 2, c0 = Math.cos(a0), s0 = Math.sin(a0), c1 = Math.cos(a1), s1 = Math.sin(a1);
      // Corners: outer i, inner i, outer i+1, inner i+1.
      const x0 = centre.x + c0 * radius, z0 = centre.z + s0 * radius, x1 = centre.x + c0 * inner, z1 = centre.z + s0 * inner;
      const x2 = centre.x + c1 * radius, z2 = centre.z + s1 * radius, x3 = centre.x + c1 * inner, z3 = centre.z + s1 * inner;
      const y0 = this.height(x0, z0, centreY), y1 = this.height(x1, z1, centreY), y2 = this.height(x2, z2, centreY), y3 = this.height(x3, z3, centreY);
      this.vertex(x0, y0, z0, alpha); this.vertex(x1, y1, z1, alpha); this.vertex(x3, y3, z3, alpha);
      this.vertex(x0, y0, z0, alpha); this.vertex(x3, y3, z3, alpha); this.vertex(x2, y2, z2, alpha);
    }
  }
  /** A small triangle just outside a ring of `radius`, pointing along `facing`. */
  private notch(centre: Point, facing: number, radius: number, alpha: number) {
    this.reserve(3);
    const ax = Math.sin(facing), az = Math.cos(facing), base = radius + NOTCH_BASE, tip = radius + NOTCH_TIP;
    const bx = centre.x + ax * base, bz = centre.z + az * base, tx = centre.x + ax * tip, tz = centre.z + az * tip, centreY = this.ground0(centre.x, centre.z);
    this.vertex(bx - az * NOTCH_HALF, this.height(bx - az * NOTCH_HALF, bz + ax * NOTCH_HALF, centreY), bz + ax * NOTCH_HALF, alpha);
    this.vertex(bx + az * NOTCH_HALF, this.height(bx + az * NOTCH_HALF, bz - ax * NOTCH_HALF, centreY), bz - ax * NOTCH_HALF, alpha);
    this.vertex(tx, this.height(tx, tz, centreY), tz, alpha);
  }
  /** Tells the mesh what was written and uploads only that part. */
  private flush() {
    if (!this.count) { this.mesh.visible = false; return; }
    const geometry = this.mesh.geometry;
    for (const [name, size] of [['position', 3], ['color', 4]] as const) {
      const attribute = geometry.getAttribute(name) as THREE.BufferAttribute;
      attribute.clearUpdateRanges(); attribute.addUpdateRange(0, this.count * size); attribute.needsUpdate = true;
    }
    geometry.setDrawRange(0, this.count);
    this.mesh.visible = true;
  }
}
